import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Clock, CheckCircle, XCircle, DollarSign, Mail } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { format, parseISO } from 'date-fns';
import { formatCurrency } from '@/lib/currency';
import { useMyPaymentRequests } from '@/hooks/finance/useMyPaymentRequests';
import { PaymentRequestStatus } from '@/types/database/payment-requests';
import { useAuth } from '@/contexts/AuthContext';

const statusConfig: Record<PaymentRequestStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  draft: { label: 'Draft', variant: 'secondary', icon: <FileText className="h-3 w-3" /> },
  pending_approval: { label: 'Pending Approval', variant: 'outline', icon: <Clock className="h-3 w-3" /> },
  approved: { label: 'Approved', variant: 'default', icon: <CheckCircle className="h-3 w-3" /> },
  rejected: { label: 'Rejected', variant: 'destructive', icon: <XCircle className="h-3 w-3" /> },
  paid: { label: 'Paid', variant: 'default', icon: <DollarSign className="h-3 w-3" /> },
  cancelled: { label: 'Cancelled', variant: 'secondary', icon: <XCircle className="h-3 w-3" /> },
};

type TabValue = 'all' | 'pending_approval' | 'approved' | 'paid' | 'rejected';

const MyPaymentRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState<TabValue>('all');
  const [search, setSearch] = useState('');

  const statusFilter = tab === 'all' ? undefined : (tab as PaymentRequestStatus);
  const { data: requests = [], isLoading } = useMyPaymentRequests(statusFilter);

  const counts = useMyPaymentRequests();
  const countsBy = useMemo(() => {
    const c = { all: 0, pending_approval: 0, approved: 0, paid: 0, rejected: 0 } as Record<string, number>;
    (counts.data || []).forEach(r => {
      c.all += 1;
      if (c[r.status] !== undefined) c[r.status] += 1;
    });
    return c;
  }, [counts.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter(r =>
      r.request_number?.toLowerCase().includes(q) ||
      r.title?.toLowerCase().includes(q) ||
      r.vendor_name?.toLowerCase().includes(q) ||
      r.department?.name?.toLowerCase().includes(q)
    );
  }, [requests, search]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground">My Payment Requests</h1>
            <p className="text-muted-foreground">Track the status of payment requests you submitted</p>
            {user?.email && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                <Mail className="h-3 w-3" />
                Showing requests submitted by <span className="font-medium text-foreground">{user.email}</span> (signed-in or via public form)
              </div>
            )}
          </div>
          <Button onClick={() => navigate('/my-requests/new')}>
            <Plus className="h-4 w-4 mr-2" /> New Request
          </Button>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
          <TabsList className="grid grid-cols-5 w-full max-w-3xl">
            <TabsTrigger value="all">All ({countsBy.all})</TabsTrigger>
            <TabsTrigger value="pending_approval">Pending ({countsBy.pending_approval})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({countsBy.approved})</TabsTrigger>
            <TabsTrigger value="paid">Paid ({countsBy.paid})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({countsBy.rejected})</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by request #, title, vendor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{filtered.length} request{filtered.length === 1 ? '' : 's'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {isLoading && (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            )}

            {!isLoading && filtered.length === 0 && (
              <div className="text-center py-10 text-muted-foreground">
                No payment requests found.
              </div>
            )}

            {!isLoading && filtered.map((r) => {
              const cfg = statusConfig[r.status];
              return (
                <button
                  key={r.id}
                  onClick={() => navigate(`/finance/accounting/payment-requests/${r.id}`)}
                  className="w-full text-left border rounded-lg p-4 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-muted-foreground">{r.request_number}</span>
                        <Badge variant={cfg.variant} className="gap-1">
                          {cfg.icon} {cfg.label}
                        </Badge>
                      </div>
                      <div className="font-medium mt-1 truncate">{r.title || '(no title)'}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {r.department?.name || '—'} · {r.vendor_name || '—'} · {r.created_at ? format(parseISO(r.created_at), 'MMM d, yyyy') : ''}
                        {r.requester_email && (
                          <> · <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{r.requester_email}</span></>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(r.total_amount || 0, r.currency || 'ETB')}</div>
                      {r.status === 'paid' && r.paid_at && (
                        <div className="text-xs text-muted-foreground">Paid {format(parseISO(r.paid_at), 'MMM d')}</div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MyPaymentRequestsPage;
